import { useMemo, useState } from "react";
import { Building2, Network, Edit, Eye, PowerOff, Power } from "lucide-react";

import { ClienteDTO } from "@/types/cliente.type";
import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { BooleanBadge } from "@/components/table/BooleanBadge";
import { ActionMenu, ActionMenuItem } from "@/components/shared/ActionMenu";
import { PermissaoEnum, ClienteSegmentoMap, ClienteFiltrosOptions } from "@workflow/types";
import { CLIENTE_SEGMENTO_ICON_MAP } from "@/constants/clienteSegmento";

import { clientesQueries, clientesKeys } from "@/services/api/queries/clientes.queries";
import { useClienteActions } from "@/hooks/clientes/use-cliente-actions";

import { ClienteEditFormDialog } from "./ClienteEditFormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClienteFiltrosCriteria } from "@workflow/types";
import { format } from "date-fns";
import { ClienteFiliaisDialog } from "./ClienteFiliaisDialog";
import { PersonBadge } from "@/components/shared/PersonBadge";

interface ClienteListTableProps {
  storageKey: string;
}

export function ClienteListTable({ storageKey }: ClienteListTableProps) {
  // Camada de dados padronizada. `useClienteActions` já invalida `clientes-matriz-list`,
  // então as mutações refrescam a lista sozinhas (sem setState local).
  const { tableProps, isError, invalidate } = useServerDataTable<
    ClienteDTO,
    ClienteFiltrosOptions,
    ClienteFiltrosCriteria
  >({
    queryOptions: clientesQueries.list,
    filters: clientesQueries.filtros,
    invalidateKey: clientesKeys.list,
  });

  // Estados dos modais
  const [clienteToEdit, setClienteToEdit] = useState<ClienteDTO | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const [clienteToToggleStatus, setClienteToToggleStatus] = useState<ClienteDTO | null>(null);
  const [isStatusAlertOpen, setIsStatusAlertOpen] = useState(false);

  const [clienteToViewFiliais, setClienteToViewFiliais] = useState<ClienteDTO | null>(null);
  const [isViewFiliaisOpen, setIsViewFiliaisOpen] = useState(false);

  const { inactivateCliente, reactivateCliente, isTogglingStatus } = useClienteActions();

  const handleConfirmStatusToggle = async () => {
    if (!clienteToToggleStatus) return;

    try {
      if (clienteToToggleStatus.ativo) {
        await inactivateCliente(clienteToToggleStatus.id);
      } else {
        await reactivateCliente(clienteToToggleStatus.id);
      }
    } finally {
      setIsStatusAlertOpen(false);
      setClienteToToggleStatus(null);
    }
  };

  const clienteColumns = useMemo<Column<ClienteDTO>[]>(
    () => [
      {
        type: "headless",
        key: "actions",
        title: "Ações",
        visible: true,
        width: "min-w-[40px]",
        render: (cliente) => {
          const actions: ActionMenuItem[] = [
            {
              label: "Ver Filiais",
              icon: <Eye className="h-4 w-4" />,
              onClick: () => {
                setClienteToViewFiliais(cliente);
                setIsViewFiliaisOpen(true);
              },
              disabled: !cliente.filiais || cliente.filiais.length === 0,
            },
            {
              label: "Editar Cliente",
              icon: <Edit className="h-4 w-4" />,
              onClick: () => {
                setClienteToEdit(cliente);
                setIsFormOpen(true);
              },
              permissions: [PermissaoEnum.ADMIN],
            },
            {
              label: "Separador",
              separator: true,
            },
            {
              label: cliente.ativo ? "Inativar" : "Reativar",
              icon: cliente.ativo ? (
                <PowerOff className="h-4 w-4" />
              ) : (
                <Power className="h-4 w-4" />
              ),
              danger: cliente.ativo,
              onClick: () => {
                setClienteToToggleStatus(cliente);
                setIsStatusAlertOpen(true);
              },
              permissions: [PermissaoEnum.ADMIN],
            },
          ];

          return <ActionMenu items={actions} />;
        },
      },
      {
        key: "cliente.id",
        title: "ID",
        sortable: true,
        width: "min-w-[80px]",
        render: (cliente) => <span className="font-medium text-primary">{cliente.id}</span>,
        raw: (cliente) => cliente.id.toString(),
      },
      {
        key: "cliente.nome",
        title: "Cliente (Matriz)",
        sortable: true,
        width: "min-w-[250px]",
        render: (cliente) => (
          <div className="flex items-center gap-2 py-1">
            <Building2 className="max-h-4 min-h-4 min-w-4 max-w-4 text-muted-foreground opacity-70" />
            <span className="truncate font-semibold text-foreground" title={cliente.nome}>
              {cliente.nome}
            </span>
          </div>
        ),
        raw: (cliente) => cliente.nome,
      },
      {
        key: "cliente.grupo",
        title: "Grupo Correspondente",
        sortable: true,
        width: "min-w-[200px]",
        render: (cliente) => (
          <span className="text-sm text-muted-foreground">{cliente.grupo || "-"}</span>
        ),
        raw: (cliente) => cliente.grupo || "",
      },
      {
        key: "cliente.segmento",
        title: "Segmento",
        width: "min-w-[180px]",
        sortable: true,
        render: (cliente) => {
          if (!cliente.segmento) return <span className="text-sm text-muted-foreground">-</span>;
          const Icon = CLIENTE_SEGMENTO_ICON_MAP[cliente.segmento];
          return (
            <div className="flex items-center gap-2 text-sm">
              <Icon className="h-4 w-4 text-muted-foreground opacity-70" />
              <span className="truncate text-foreground">
                {ClienteSegmentoMap[cliente.segmento]}
              </span>
            </div>
          );
        },
        raw: (cliente) => (cliente.segmento ? ClienteSegmentoMap[cliente.segmento] : ""),
      },
      {
        key: "cliente.filiais",
        title: "Filiais Vinculadas",
        width: "min-w-[150px]",
        render: (cliente) => (
          <div className="flex items-center gap-1.5 text-sm">
            <Network className="h-4 w-4 text-purple-500/70" />
            <span className="font-medium">{cliente.filiais?.length || 0} filiais</span>
          </div>
        ),
      },
      {
        key: "cliente.ativo",
        title: "Status",
        sortable: true,
        width: "min-w-[100px]",
        render: (cliente) => <BooleanBadge value={cliente.ativo} />,
        raw: (cliente) => cliente.ativo.toString(),
      },
      {
        key: "criadoPor.nome",
        title: "Criado por",
        sortable: true,
        render: (cliente) =>
          cliente?.criadoPor ? <PersonBadge pessoa={cliente.criadoPor} isCompact={true} /> : "-",
        raw: (cliente) => cliente.criadoPor?.nome || "",
      },
      {
        key: "cliente.criadoEm",
        title: "Criado em",
        sortable: true,
        width: "min-w-[120px]",
        render: (cliente) => (
          <span className="text-sm text-muted-foreground">
            {cliente.criadoEm ? format(new Date(cliente.criadoEm), "dd/MM/yyyy HH:mm") : "-"}
          </span>
        ),
        raw: (cliente) => cliente.criadoEm?.toString() || "",
      },
    ],
    [],
  );

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey={storageKey}
        columns={clienteColumns}
        emptyMessage={
          isError
            ? "Não foi possível carregar os clientes. Tente recarregar."
            : "Nenhum cliente matriz encontrado."
        }
        defaultSortColumn="cliente.id"
        defaultSortOrder="DESC"
        searchPlaceholder="Pesquisar clientes por nome ou grupo..."
        className="border-none"
        rowClassName={(cliente) => (cliente.ativo ? "" : "opacity-30")}
      />

      <ClienteEditFormDialog
        open={isFormOpen}
        onOpenChange={(isOpen) => {
          setIsFormOpen(isOpen);
          if (!isOpen) setClienteToEdit(null);
        }}
        clienteToEdit={clienteToEdit}
        onSuccess={() => invalidate()}
      />

      <ClienteFiliaisDialog
        open={isViewFiliaisOpen}
        onOpenChange={(isOpen) => {
          setIsViewFiliaisOpen(isOpen);
          if (!isOpen) setClienteToViewFiliais(null);
        }}
        onClose={() => {
          setIsViewFiliaisOpen(false);
          setClienteToViewFiliais(null);
        }}
        cliente={clienteToViewFiliais}
      />

      <AlertDialog open={isStatusAlertOpen} onOpenChange={setIsStatusAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmar {clienteToToggleStatus?.ativo ? "Inativação" : "Reativação"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Você tem certeza que deseja {clienteToToggleStatus?.ativo ? "inativar" : "reativar"} o
              cliente{" "}
              <span className="font-bold text-foreground">{clienteToToggleStatus?.nome}</span>?
              {clienteToToggleStatus?.ativo &&
                " Filiais vinculadas também poderão perder o acesso a novos faturamentos."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTogglingStatus}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmStatusToggle();
              }}
              disabled={isTogglingStatus}
              className={
                clienteToToggleStatus?.ativo
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }
            >
              {isTogglingStatus ? "Aguarde..." : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
